const cylinderSizeMap = {
    "X0.4": "Non refillable canister (handheld)",
    "X2": "2 Litre (handheld refillable)",
    "X7": "7 Litre (knee height)",
    "X10": "10 Litre (knee height)",
    "X20": "20 Litre (waist height)",
    "X47": "47 Litre (shoulder height)",
    "X50": "50 Litre (shoulder height)",
    "X108": "47 Litre (Propane only)",
    "11X47": "Pack of 11x 47 Litre cylinders",
    "12X50": "Pack of 12x 50 Litre cylinders",
    "NR": "Non-Refill Canister/Aerosol",
    "NR34": "Non-Refill 34L Canister",
    "NR58": "Non-Refill 58L Canister",
    "NR110": "Non-Refill 110L Canister",
}


/**
 * 
 * helper object containing reusable functions
 * 
 */
export const helper = {
    /**
     * 
     * Fetches matching mixture gases according to selected application / market stored in state
     * 
     * @param {object} mixtureGasData 
     * @param {object} state 
     * @returns {array} 
     */
    getAssociatedMixtureGases: (mixtureGasData, application) => {
        return mixtureGasData.filter((mixtureGas) => {
            if( mixtureGas.Market === application ) {
                return true;
            }
            return false;
        });
    }
}

/**
 * 
 * Gets all product data as an array of objects for the matched product only
 * 
 * @param {object} mixtureGasData 
 * @param {array} productCodes 
 * @returns {array}
 */
export function getMatchedProductsData(mixtureGasData, productCodes, application = null) {
    return mixtureGasData.filter(product => { 
        if( application !== null ) {
            return productCodes.includes(product.PR) && application === product.Market;
        } else {
            return productCodes.includes(product.PR)
        }
    })
}


/**
 * Get unique components relevant to the selected market / applicaton
 * 
 * @param {object} mixtureGases 
 * @returns {array}
 */
export function getUniqueComponents(mixtureGases) {
    const allComponents = [];

    mixtureGases.forEach( mixtureGas => {
        mixtureGas.components.forEach(comp => {
            if( comp.component !== '' ) {
                allComponents.push(comp.component);
            }
        })
    })

    return allComponents.filter((value, index, self) => self.indexOf(value) === index);
}

/**
 * Returns product name formatted 
 * 
 * @param {object} product 
 * @returns {string}
 */
export function generateProductName(product) {
    const comps = product.components.filter((comp) => comp.component !== "");
    let name = '';
    comps.forEach((comp) => {
        const compName = `${comp.concentration} ${comp.unit} ${comp.component}`;
        if( name === '') {
            name += `${product.base} + ${compName}`;
        } else {
            name += ` + ${compName}`;
        }
    })

    return name;
}

/**
 * Generate custom product name
 * Layout is different than off the shelf components
 * 
 * @param {object} product 
 * @returns {string}
 */
export function generateCustomProductName(product) {
    const comps = product.components.filter((comp) => comp.component !== "");
    let name = `Custom Mix: <br/>`; 

    comps.forEach((comp) => {
        const compName = `${comp.concentration} ${comp.unit} ${comp.component}`;

        name += `- ${compName} <br/>`;
        
    })
    
    name += `- ${product.base} (Balance gas)`;

    return name;
}

export function getBalanceGases(data) {
    return data.map(item => item.base).filter((item, index, self) => self.indexOf(item) === index).sort();
}

export function getCylinderSizes(data) {
    return data.map(item => item.size).filter((item, index, self) => self.indexOf(item) === index).sort();
}

export function getCylinderSizeFormatted(size) {
    return cylinderSizeMap[size];
}

/**
 * Returns a list of unique accreditation types
 * 
 * @param {object} data 
 * @returns {array}
 */
export function getAccreditationTypes(data) {
    return data.map(elem => elem.Type).filter((item, index, self) => self.indexOf(item) === index).sort();
}

/**
 * Convert value to ppb (parts per billion)
 * 
 * @param {float} val 
 * @param {string} unit 
 * @returns {float}
 */
export function convertToppb(val, unit) {
    const x = parseFloat(val);

    switch(unit) {
        case '%':
            return x * 10000 * 1000;
        case 'ppm':
            return x * 1000;
        default:
            return x;
    }
}

/**
 * Determines if given number is between a range
 * 
 * @param {float} x 
 * @param {float} min 
 * @param {float} max 
 * @returns {boolean}
 */
export function isInRange(x, min, max) {
    return (x - min) * (x - max) <= 0 
}

export function removeEmptyComponents(components) {
    const filteredComponents = components.filter(item => item.component !== '');
    return filteredComponents;
}

export function sortComponents(comp) {
    let sorted = comp.sort((a, b) => {
        return a.component.localeCompare(b.component);
    })
    return sorted;
}

/**
 * Compares two component arrays and ensures the same components are in each
 * 
 * @param {array} comp1  custom product
 * @param {array} comp2 
 * @returns {boolean}
 */
export function checkMixtureComponentsMatch(comp1, comp2) {
    let comp1ComponentsList = comp1.map(item => item.component.split("-")[0].trim().toLowerCase()).sort();
    let comp2ComponentsList = comp2.map(item => item.component.toLowerCase()).sort();

    // Arrays are the same length so compare values by looping over 1 array
    for (let index = 0; index < comp1ComponentsList.length; index++) {
        if( comp1ComponentsList[index] !== comp2ComponentsList[index] ) {
            return false;
        }   
    }

    return true;
}

/**
 * Compare two component arrays to ensure each component is within range
 * 
 * @param {array} comp1 Custom product components
 * @param {array} comp2 Off-the-shelf product components
 * @param {int} range Allowed range for match
 */
 export function checkMixtureComponentsInRange(comp1, comp2, range) {
    let inRange = true;

    for (let index = 0; index < comp1.length; index++) {
        const customProdConcentration = parseFloat(comp1[index].concentration);
        const stockedProdConcentration = parseFloat(comp2[index].concentration);

        if( comp1[index].unit === comp2[index].unit ) {
            // Units match, no need to convert to ppb
            const rangeAmount = (customProdConcentration * (range / 100));
            if( !isInRange(stockedProdConcentration, customProdConcentration - rangeAmount, customProdConcentration + rangeAmount ) ) {
                inRange = false;
            }
        } else {
            // Units do not match, convert them to ppb (smallest demoninator) and then check range
            const customProdConcentrationPpb = convertToppb(customProdConcentration, comp1[index].unit);
            const stockedProdConcentrationPpb = convertToppb(stockedProdConcentration, comp2[index].unit);
            const rangeAmount = (customProdConcentrationPpb * (range / 100));

            if( !isInRange(stockedProdConcentrationPpb, customProdConcentrationPpb - rangeAmount, customProdConcentrationPpb + rangeAmount ) ) {
                inRange = false;
            }
        }
    }

    return inRange;
}